import type { KeycloakTokenParsed } from 'keycloak-js';
import type Keycloak from 'keycloak-js';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type {
  TrackResultDTO,
  UpdateUserDTO,
  UserDetailsUpdateEventDTO,
  UserResultDTO,
} from '@nora-monorepo/nora-api-client';
import type { PositionResponse } from '@nora-monorepo/track-api-web-client';

import { useApiStore } from '#ui-shared/stores/api/api-store';
import { useSseStore } from '#ui-shared/stores/sse-store';
import { UserLoggingContext } from '#ui-shared/utils/logger/contexts/log-contexts';
import { appLogger } from '#ui-shared/utils/logger/LoggerClass';
import { TrackingUtilities } from '#ui-shared/utils/tracking-utilities';

export const useGenericUserStore = defineStore('genericUserStore', () => {
  const keycloak = ref<Keycloak>();
  const user = ref<UserResultDTO>();

  const token = ref<string>();
  const tokenParsed = ref<KeycloakTokenParsed>();

  const userIsLoading = ref(true);

  const api = useApiStore();
  const sseStore = useSseStore();

  const currentUserId = computed(() => {
    return user.value?.id;
  });

  function getLastTrackResult(): TrackResultDTO | undefined {
    if (!user.value || !user.value.lastTrack) {
      appLogger.debug({
        message: `No user or last track available`,
        context: {
          user: new UserLoggingContext({ userId: currentUserId.value || 'unknown' }),
        },
      });
      return undefined;
    }

    return user.value.lastTrack;
  }

  async function loadUser(): Promise<void> {
    const fetchedUser = await api.unwrap(api.nexusUser.getCurrentUser());

    if (!fetchedUser.data) {
      throw new Error('User not loaded');
    }

    user.value = fetchedUser.data;
    await sseStore.startListening();

    userIsLoading.value = false;
  }

  function updateLastTrack(position: PositionResponse): void {
    if (!user.value) {
      appLogger.warn({
        message: `Received position update but no user is loaded`,
        context: {
          positionProducerId: position.producerId,
        },
      });
      return;
    }

    if (user.value.id !== position.producerId) {
      return;
    }

    user.value.lastTrack = TrackingUtilities.positionResponseToTrackDTO(position);
  }

  async function updateUserDetailsEvent(updatedUser: UserDetailsUpdateEventDTO): Promise<void> {
    if (!user.value) {
      return;
    }

    if (updatedUser.userId !== user.value.id) {
      return;
    }

    if (updatedUser.customCallSign !== undefined) {
      user.value.customCallSign = updatedUser.customCallSign;
    }
  }

  async function updateOwnUser(data: UpdateUserDTO): Promise<void> {
    try {
      await api.nexusUser.updateOwnUser({ updateUserDTO: data });
      await loadUser();
    }
    catch (error) {
      console.error('Failed to update user:', error);
    }
  }

  async function updateUser(userId: string, data: UpdateUserDTO): Promise<void> {
    try {
      await api.nexusUser.updateUser({
        userId,
        updateUserDTO: data,
      });
    }
    catch (error) {
      console.error('Failed to update user:', error);
    }
  }

  function isCurrentUser(userId: string): boolean {
    return userId.toLowerCase() === currentUserId.value?.toLowerCase();
  }

  return {
    keycloak,
    user,
    token,
    tokenParsed,
    currentUserId,
    userIsLoading,
    loadUser,
    updateLastTrack,
    updateOwnUser,
    updateUserDetailsEvent,
    updateUser,
    getLastTrackResult,
    isCurrentUser,
  };
});
